import React, { useEffect, useState, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Mail, RefreshCw, Loader2, CheckCircle, AlertTriangle, XCircle, Send } from 'lucide-react';

const CHANNEL_LABEL = { sendgrid: 'SendGrid', managed: 'Managed', none: 'Down' };

const StatusDot = ({ ok, warn }) => (
  <span className={`inline-block w-2 h-2 rounded-full ${ok ? 'bg-emerald-500' : warn ? 'bg-amber-500' : 'bg-red-500'}`} />
);

export const EmailHealthBadge = () => {
  const [health, setHealth] = useState(null);
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(null);

  const resend = async (logId) => {
    setResending(logId);
    try {
      const r = await api.post(`/admin/email/resend/${logId}`);
      if (r.data.email_sent) {
        toast.success(`Email resent to ${r.data.to_email}`);
      } else {
        toast.error('Resend attempted but delivery failed again — check channel health');
      }
      loadHealth();
      loadLogs(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resend failed');
    } finally {
      setResending(null);
    }
  };

  const loadHealth = useCallback(async () => {
    try {
      const r = await api.get('/admin/email/health');
      setHealth(r.data);
    } catch (e) {
      setHealth({ active_channel: 'none', sendgrid: { status: 'error', detail: 'health check failed' }, managed: { status: 'unknown' } });
    }
  }, []);

  const loadLogs = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const r = await api.get('/admin/email/logs', { params: { limit: 100, ...(q ? { search: q } : {}) } });
      setLogs(r.data.logs || []);
      setLogsTotal(r.data.total || 0);
    } catch (e) {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadHealth(); }, [loadHealth]);
  useEffect(() => { if (open) { loadHealth(); loadLogs(search); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const active = health?.active_channel || 'none';
  const badgeClass = active === 'sendgrid'
    ? 'bg-emerald-600 hover:bg-emerald-700'
    : active === 'managed'
      ? 'bg-sky-600 hover:bg-sky-700'
      : 'bg-red-600 hover:bg-red-700';

  const sgStatus = health?.sendgrid?.status;
  const sgOk = sgStatus === 'ok';

  return (
    <>
      <Badge
        data-testid="email-health-badge"
        className={`gap-1 cursor-pointer ${badgeClass}`}
        onClick={() => setOpen(true)}
        title="Email channel health — click for delivery log"
      >
        <Mail className="w-3 h-3" /> Email: {health ? CHANNEL_LABEL[active] : '…'}
      </Badge>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="email-health-dialog" className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" /> Email Delivery Health
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div data-testid="email-health-sendgrid" className={`rounded-lg border p-3 ${sgOk ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
              <div className="flex items-center gap-2 font-semibold text-sm">
                <StatusDot ok={sgOk} warn={!sgOk && sgStatus !== 'invalid_key'} />
                SendGrid {active === 'sendgrid' && <span className="text-[10px] uppercase tracking-wider text-emerald-700">active</span>}
                {sgOk && active !== 'sendgrid' && <span className="text-[10px] uppercase tracking-wider text-gray-500">standby</span>}
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {sgOk && active !== 'sendgrid' ? `${health?.sendgrid?.detail} — on standby, managed email is the preferred channel.`
                  : sgOk ? health?.sendgrid?.detail
                  : sgStatus === 'no_credits' ? 'No email credits — plan expired or exhausted. Upgrade at app.sendgrid.com to resume.'
                  : health?.sendgrid?.detail || '—'}
              </p>
            </div>
            <div data-testid="email-health-managed" className={`rounded-lg border p-3 ${health?.managed?.status === 'configured' ? 'border-sky-200 bg-sky-50' : 'border-red-200 bg-red-50'}`}>
              <div className="flex items-center gap-2 font-semibold text-sm">
                <StatusDot ok={health?.managed?.status === 'configured'} />
                Emergent Managed Email {active === 'managed' && <span className="text-[10px] uppercase tracking-wider text-sky-700">active</span>}
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {health?.managed?.status === 'configured'
                  ? (active === 'managed'
                      ? 'Primary channel — all platform email currently goes out through Emergent-managed email.'
                      : 'Configured — used automatically when SendGrid cannot send.')
                  : 'Not configured.'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 text-sm" data-testid="email-health-stats">
            <span className="flex items-center gap-1 text-emerald-700">
              <CheckCircle className="w-4 h-4" /> {health?.sent_24h ?? 0} sent (24h)
            </span>
            <span className="flex items-center gap-1 text-red-600">
              <XCircle className="w-4 h-4" /> {health?.failed_24h ?? 0} failed (24h)
            </span>
            {health?.last_sent && (
              <span className="text-xs text-gray-500 truncate">
                Last: {health.last_sent.to_email} · {new Date(health.last_sent.created_at).toLocaleString()}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Input
              data-testid="email-log-search"
              placeholder="Search recipient or subject…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadLogs(search)}
              className="h-8"
            />
            <Button data-testid="email-log-refresh" variant="outline" size="sm" onClick={() => { loadHealth(); loadLogs(search); }}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="grid grid-cols-[150px_1fr_90px_70px] gap-2 px-3 py-2 bg-gray-50 text-[11px] font-semibold uppercase tracking-wider text-gray-500 border-b">
              <span>Time</span><span>Recipient / Subject</span><span>Channel</span><span>Status</span>
            </div>
            <div className="max-h-72 overflow-y-auto divide-y" data-testid="email-log-list">
              {logs.length === 0 && (
                <p className="text-sm text-gray-500 p-4 text-center" data-testid="email-log-empty">
                  {loading ? 'Loading…' : 'No delivery receipts yet. Receipts are recorded for every email the platform sends.'}
                </p>
              )}
              {logs.map((l) => (
                <div key={l.id} data-testid={`email-log-row-${l.id}`} className="grid grid-cols-[150px_1fr_90px_70px] gap-2 px-3 py-2 text-xs items-start">
                  <span className="text-gray-500 font-mono">{new Date(l.created_at).toLocaleString()}</span>
                  <span className="min-w-0">
                    <span className="font-medium block truncate">{l.to_email}</span>
                    <span className="text-gray-500 block truncate">{l.subject}</span>
                    {l.status === 'failed' && l.error && (
                      <span className="text-red-500 block truncate" title={l.error}>{l.error}</span>
                    )}
                  </span>
                  <span className="capitalize text-gray-600">{l.channel}</span>
                  {l.status === 'sent' ? (
                    <span className="flex items-center gap-1 text-emerald-600"><CheckCircle className="w-3 h-3" /> Sent</span>
                  ) : (
                    <span className="flex flex-col gap-1">
                      <span className="flex items-center gap-1 text-red-600"><AlertTriangle className="w-3 h-3" /> Failed</span>
                      {l.has_html && !l.resent_at && (
                        <Button
                          data-testid={`email-log-resend-${l.id}`}
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-[11px] gap-1 border-sky-300 text-sky-700 hover:bg-sky-50"
                          disabled={resending === l.id}
                          onClick={() => resend(l.id)}
                        >
                          {resending === l.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                          Resend
                        </Button>
                      )}
                      {l.resent_at && (
                        <span className={`text-[10px] ${l.resend_result === 'sent' ? 'text-emerald-600' : 'text-red-500'}`}>
                          Resent: {l.resend_result}
                        </span>
                      )}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
          <p className="text-[11px] text-gray-400">{logsTotal} receipt{logsTotal === 1 ? '' : 's'} total · "Sent" means the provider accepted the email for delivery.</p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmailHealthBadge;
